import { catalogSchema, rawCatalog, type Catalog } from './catalog.ts';
import type { CallingId, Discipline, SkillId, TownId } from './ids.ts';

export * from './ids.ts';
export * from './schema.ts';
export { blocklist } from './data/blocklist.ts';
export { catalogSchema, type Catalog } from './catalog.ts';
/** Deeds are imported from `@wyn/content/deeds`: kept out of this entry point so the web bundle never carries the hidden ones. */
export type { Deed, DeedTest } from './deeds.ts';

/** Content is validated by `pnpm build` (scripts/validate.ts) and in tests; at runtime it is trusted. */
export const catalog: Catalog = rawCatalog as Catalog;

export type Item = Catalog['items'][number];
export type Calling = Catalog['callings'][number];
export type Town = Catalog['towns'][number];
export type Venue = Catalog['venues'][number];
export type Skill = Catalog['skills'][number];
export type Crime = Catalog['crimes'][number];
export type Shop = Catalog['shops'][number];
export type Minigame = Catalog['minigames'][number];
export type MinigameEngine = Minigame['engine'];
/** A minigame narrowed to one engine, and one of its activities. */
export type MinigameOf<E extends MinigameEngine> = Extract<Minigame, { engine: E }>;
export type ActivityOf<E extends MinigameEngine> = MinigameOf<E>['activities'][number];
export type Activity = Minigame['activities'][number];
export type Mastery = Catalog['masteries'][number];
export type PassiveEffect = Mastery['passive']['effect'];
export type Recipe = Catalog['recipes'][number];

const byKey = <T, K extends string>(xs: T[], key: (x: T) => K) => new Map(xs.map((x) => [key(x), x] as const));
const itemMap = byKey(catalog.items, (i) => i.key);
const callingMap = byKey(catalog.callings, (c) => c.id);
const townMap = byKey(catalog.towns, (t) => t.id);
const crimeMap = byKey(catalog.crimes, (c) => c.key);
const venueMap = byKey(catalog.venues, (v) => v.key);
const minigameMap = byKey(catalog.minigames, (m) => m.venue);

/** Every icon name the catalog uses, for checking against the icon set and bundling only those. */
export function catalogIcons(): string[] {
  const { towns, venues, items, skills, callings, crimes, shops } = catalog;
  const all = [...towns, ...venues, ...items, ...skills, ...callings, ...crimes, ...shops].map((x) => x.icon);
  return [...new Set(all)].sort();
}

export function getItem(key: string): Item {
  const item = itemMap.get(key);
  if (!item) throw new Error(`unknown item ${key}`);
  return item;
}
export const getCalling = (id: CallingId): Calling => callingMap.get(id)!;
export const getTown = (id: TownId): Town => townMap.get(id)!;
export const capitalTown = (): Town => catalog.towns.find((t) => t.capital)!;
export const venuesIn = (town: TownId): Venue[] => catalog.venues.filter((v) => v.town === town);
export const skillsIn = (discipline: Skill['discipline']): Skill[] => catalog.skills.filter((s) => s.discipline === discipline);
export const skill = (id: SkillId): Skill => catalog.skills.find((s) => s.id === id)!;

export function travelMinutes(from: TownId, to: TownId): number | null {
  if (from === to) return 0;
  const r = catalog.routes.find((x) => (x.from === from && x.to === to) || (x.from === to && x.to === from));
  return r ? r.minutes : null;
}

export const findItem = (key: string): Item | undefined => itemMap.get(key);
export const findCrime = (key: string): Crime | undefined => crimeMap.get(key);
export const crimesIn = (town: TownId): Crime[] => catalog.crimes.filter((c) => !c.towns || c.towns.includes(town));
export const shopsIn = (town: TownId): Shop[] => catalog.shops.filter((s) => s.towns.includes(town));
/** Towns with a direct road from `from`, nearest first. */
export const roadsFrom = (from: TownId): { to: TownId; minutes: number }[] =>
  catalog.routes
    .flatMap((r) => (r.from === from ? [{ to: r.to, minutes: r.minutes }] : r.to === from ? [{ to: r.from, minutes: r.minutes }] : []))
    .sort((a, b) => a.minutes - b.minutes);

export const findVenue = (key: string): Venue | undefined => venueMap.get(key);
/** The minigame played at a venue; undefined while its doors are still shut. */
export const minigameAt = (venueKey: string): Minigame | undefined => minigameMap.get(venueKey);
export const findActivity = (venueKey: string, activityKey: string): Activity | undefined =>
  (minigameMap.get(venueKey)?.activities as Activity[] | undefined)?.find((a) => a.key === activityKey);

/** A Discipline's four titles, lowest tier first. */
export const masteriesOf = (d: Discipline): Mastery[] => catalog.masteries.filter((m) => m.discipline === d).sort((a, b) => a.tier - b.tier);
export const recipesAt = (venueKey: string): Recipe[] => catalog.recipes.filter((r) => r.venue === venueKey);
export const findRecipe = (key: string): Recipe | undefined => catalog.recipes.find((r) => r.key === key);
